import * as React from 'react';

import { Divider } from '@rbilabs/universal-components';
import { ScrollView, StyleSheet, View } from 'react-native';

import { WEB_MAX_WIDTH_STYLE } from 'components/card-modal';
import Picture from 'components/picture';
import SanityBlockRenderer from 'components/sanity-block-renderer';
import { Box, Typography } from 'components/ui';
import { useIsDesktopBreakpoint } from 'hooks/use-media-query';
import { primitive, spacing } from 'styles/constants';

import { useQuestContext } from '../loyalty-quest';
import { isQuestCompleted } from '../loyalty-quest/utils';
import { QuestProgressBar } from '../quest-progress-bar';
import { getActiveTierImage } from '../use-parse-quest-card-data/utils';

import { CrownPatternBackground } from './crown-pattern-background';
import { EndDateBadge } from './end-date-badge';
import { QuestSteps } from './quest-steps';
import { IQuestDetailContentViewProps } from './types';

const MOBILE_IMAGE_HEIGHT = 85;
const DESKTOP_IMAGE_HEIGHT = 120;

export const QuestDrawerContent = ({
  cmsQuestDetail,
  cmsQuestDetailUI,
  cmsQuestCard,
  onCloseDrawer,
}: IQuestDetailContentViewProps) => {
  const { howToRedeem } = cmsQuestDetail;
  const howToRedeemSanityContent = howToRedeem?.localeRaw;

  const { quest } = useQuestContext();
  const isDesktop = useIsDesktopBreakpoint();
  const questTitle = cmsQuestCard?.name?.locale;
  // Mirror QuestCard: prefer the active tier's header image, fall back to the
  // base quest card image. Keeps the drawer header in sync with the card.
  const { image: questImage, imageDescription: questImageAlt } = getActiveTierImage(
    quest,
    cmsQuestCard,
    cmsQuestDetail
  );
  const imageMaxHeight = isDesktop ? DESKTOP_IMAGE_HEIGHT : MOBILE_IMAGE_HEIGHT;

  return (
    <View style={styles.pageContainer}>
      <View
        style={[
          styles.questImageContainer,
          isDesktop ? styles.questImageContainerDesktop : styles.questImageContainerMobile,
        ]}
      >
        <CrownPatternBackground style={StyleSheet.absoluteFillObject} />
        {!!questImage && (
          <Picture
            maxHeight={imageMaxHeight}
            image={questImage}
            flexDirection="column"
            alt={questImageAlt || 'quest image'}
          />
        )}
      </View>
      <ScrollView contentContainerStyle={styles.scrollView}>
        <Box style={[styles.questStepsWrapper, isDesktop ? WEB_MAX_WIDTH_STYLE : {}]}>
          <View style={styles.questStepsSection}>
            <View style={styles.headerContainer}>
              {!!questTitle && (
                <Typography variant="headerThree" style={styles.title}>
                  {questTitle}
                </Typography>
              )}
              {!isQuestCompleted(quest?.status) && <EndDateBadge quest={quest} />}
            </View>

            <View style={styles.progressBar}>
              <QuestProgressBar steps={quest?.steps ?? []} />
            </View>

            <SanityBlockRenderer content={howToRedeemSanityContent} />

            <Divider />

            <QuestSteps
              cmsQuestRequirementById={cmsQuestDetail.requirementByCmsId}
              cmsQuestDetail={cmsQuestDetail}
              cmsQuestDetailUI={cmsQuestDetailUI}
              onCloseDrawer={onCloseDrawer}
              quest={quest}
            />
          </View>
        </Box>
      </ScrollView>
    </View>
  );
};

const styles = StyleSheet.create({
  pageContainer: { flex: 1 },
  headerContainer: {
    flexDirection: 'row',
    alignItems: 'flex-start',
    justifyContent: 'space-between',
    gap: spacing.$2,
  },
  title: {
    flex: 1,
    fontSize: primitive.fontSizes.lg,
  },
  scrollView: {
    justifyContent: 'center',
    alignItems: 'center',
    backgroundColor: primitive.$mayoEggWhite,
  },
  progressBar: { padding: 5 },
  questImageContainer: {
    position: 'relative',
    overflow: 'hidden',
  },
  questImageContainerMobile: { height: MOBILE_IMAGE_HEIGHT },
  questImageContainerDesktop: { height: DESKTOP_IMAGE_HEIGHT },
  // Replaces former QuestStepsSectionWrapper (VStack.withConfig) so styles
  // are co-located with the component per AGENTS.md.
  questStepsSection: {
    padding: spacing.$4,
    gap: spacing.$4,
    backgroundColor: primitive.$white,
    borderRadius: primitive.$borderRadius,
  },
  questStepsWrapper: {
    paddingBottom: spacing.$2,
    paddingHorizontal: spacing.$1,
  },
});
